import {
  TableColumn,
  TableFilters,
  TableSort,
  TableFilterValues,
} from "./types";
import { useMemo, useCallback, useEffect } from "react";
import { tablesFilters } from "../../shared/atoms";
import { generateSort, mapSort } from "./helpers";
import { generateFilter } from "@/shared/utils/filter";
import { groupArrayOfObjects } from "@/shared/utils/helpers";
import { useAtom } from "jotai";

const useTableFiltersHook = (tableName: string, filterRoutes?: string[]) => {
  const [filters, setFilters] = useAtom(tablesFilters);
  const addFilter = useCallback(
    (tableName: string, filters: TableFilterValues[]) => {
      setFilters((f) => ({
        ...f,
        [tableName]: { ...f[tableName], filters, pageNumber: 1 },
      }));
    },
    []
  );
  const addpageSize = useCallback((tableName: string, pageSize: any) => {
    setFilters((f) => ({ ...f, [tableName]: { ...f[tableName], pageSize } }));
  }, []);

  const addpageNumber = useCallback((tableName: string, pageNumber: any) => {
    setFilters((f) => ({ ...f, [tableName]: { ...f[tableName], pageNumber } }));
  }, []);

  const addSort = useCallback((tableName: string, sort: TableSort[]) => {
    setFilters((f) => ({ ...f, [tableName]: { ...f[tableName], sort } }));
  }, []);

  const filter = useMemo<TableFilters>(
    () => ({
      sort: [],
      pageSize: 10,
      pageNumber: 1,
      filters: [],
      ...filters[tableName],
    }),
    [tableName, filters]
  );

  const findFilterValue = useCallback(
    (id: string) => {
      return filter.filters.find((filter) => filter.id === id);
    },
    [filter]
  );

  const formatted = useMemo(() => {
    let group: any = [];
    const sort =
      filter?.sort?.map((s) =>
        generateFilter(s.id, [s.value], "sort", s.index, s.group)
      ) || [];
    const filters = filter.filters
      .map((f) =>
        Array.isArray(f.filter)
          ? f.filter.map((x) =>
              generateFilter(
                x.type,
                x.values,
                x.operation,
                x.index,
                x.table,
                x.forceOperation,
                x?.values_in,
                x?.values_not_in
              )
            )
          : generateFilter(
              f.filter.type,
              f.filter.values,
              f.filter.operation,
              f.filter.index,
              f.filter.table,
              f.filter.forceOperation,
              f.filter?.values_in,
              f.filter?.values_not_in
            )
      )
      .flat();
    group = filters.concat(sort);
    group = groupArrayOfObjects(group, "table");
    return {
      ...filter,
      filters: group,
      sort: undefined,
    };
  }, [filter]);

  const getSort = useCallback(
    (id: string) => filter.sort.find((s) => s.id === id),
    [filter.sort]
  );

  const handleColumnClick = useCallback(
    (column: TableColumn, forceValue?: boolean) => {
      const isSorted = getSort(column.id) !== undefined;
      const sort = generateSort(
        column.id,
        forceValue || isSorted,
        column.group,
        column.index,
        column.isNumeric || false,
        column.reset || []
      );
      const values = mapSort(filter.sort, sort);
      return addSort(tableName, values);
    },
    [filter, tableName, addSort, getSort]
  );

  useEffect(() => {
    const key = "tableRoutes";
    const storage = () => localStorage.getItem(key);
    let savedRoutes = JSON.parse(storage()) || {};
    if (filterRoutes !== undefined)
      savedRoutes = { ...savedRoutes, [tableName]: filterRoutes };
    localStorage.setItem(key, JSON.stringify(savedRoutes));
  }, [filterRoutes, tableName]);

  return {
    addFilter,
    filter,
    filters,
    setFilters,
    addpageSize,
    addpageNumber,
    addSort,
    getSort,
    handleColumnClick,
    findFilterValue,
    formatted,
  };
};

export default useTableFiltersHook;
